import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStorageData = useCallback(async () => {
    try {
      const storedUser = localStorage.getItem('@Emprestimos:user');
      const storedToken = localStorage.getItem('@Emprestimos:token');

      if (storedUser && storedToken) {
        // Verificar se o token ainda é válido
        try {
          const response = await api.get('/auth/verify', {
            headers: {
              Authorization: `Bearer ${storedToken}`
            }
          });
          
          if (response.data.valid) {
            api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            setUser(JSON.parse(storedUser));
          } else {
            // Se o token não for válido, limpar o storage
            localStorage.removeItem('@Emprestimos:user');
            localStorage.removeItem('@Emprestimos:token');
          }
        } catch (error) {
          // Se houver erro na verificação, limpar o storage
          localStorage.removeItem('@Emprestimos:user');
          localStorage.removeItem('@Emprestimos:token');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStorageData();
  }, [loadStorageData]);

  const signIn = useCallback(async (email, senha) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        senha,
      });

      const { usuario, token } = response.data;

      if (!token) {
        throw new Error('Token não fornecido pelo servidor');
      }

      if (!usuario || !usuario.role) {
        throw new Error('Dados do usuário inválidos');
      }

      localStorage.setItem('@Emprestimos:user', JSON.stringify(usuario));
      localStorage.setItem('@Emprestimos:token', token);

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(usuario);
    } catch (error) {
      throw error.response?.data?.error || 'Erro ao fazer login';
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      localStorage.removeItem('@Emprestimos:user');
      localStorage.removeItem('@Emprestimos:token');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      console.log('Logout executado! Chaves após logout:', {
        '@Emprestimos:user': localStorage.getItem('@Emprestimos:user'),
        '@Emprestimos:token': localStorage.getItem('@Emprestimos:token'),
        'user': localStorage.getItem('user'),
        'token': localStorage.getItem('token'),
      });
      await Promise.resolve();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        signed: !!user,
        user,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
